// pages/GalleryPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import galleryData from '../data/gallery.json';
import Button from '../components/ui/Button';
import Section from '../components/layout/Section';
import Modal from '../components/ui/Modal';
import { trackCtaClick } from '../utils/tracking';

interface GalleryImage {
  id: string;
  full: string;
  thumb: string;
  alt: string;
  category?: string;
}

interface GalleryProject {
  id: number;
  name: string;
  location?: string;
  date?: string;
  description?: string;
  images: GalleryImage[];
}

// Support both v1 (flat array) and v2 ({images, projects}) formats
const isV2 = !Array.isArray(galleryData);
const allImages: GalleryImage[] = isV2 ? (galleryData as any).images : galleryData;
const projects: GalleryProject[] = isV2 ? ((galleryData as any).projects || []) : [];

// Ungrouped images = images not in any project
const projectImageIds = new Set(projects.flatMap(p => p.images.map(img => img.id)));
const ungroupedImages = allImages.filter(img => !projectImageIds.has(img.id));

const GalleryPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const openModal = (item: GalleryImage) => {
    setSelectedImage(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeModal();
    };
    if (isModalOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  // Categories from ungrouped images only (projects display separately)
  const categories = useMemo(() => {
    const cats = new Set<string>();
    ungroupedImages.forEach(item => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, []);

  const filteredUngrouped = useMemo(() => {
    if (activeCategory === 'all') return ungroupedImages;
    return ungroupedImages.filter(item => item.category === activeCategory);
  }, [activeCategory]);

  const hasProjects = projects.length > 0;
  const hasCategories = categories.length > 0;

  const renderImageCard = (item: GalleryImage) => (
    <div
      key={item.id}
      className="bg-white rounded-lg shadow-lg overflow-hidden group transform hover:scale-105 transition-transform duration-300 cursor-pointer"
      onClick={() => openModal(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openModal(item); }}
    >
      <img
        src={item.thumb}
        alt={item.alt + " - thumbnail"}
        className="w-full h-48 object-cover"
        loading="lazy"
        onError={(e) => (e.currentTarget.src = item.full)}
      />
      <div className="p-3">
        <h3 className="text-sm font-medium text-slate-800 truncate">{item.alt}</h3>
        {item.category && (
          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 rounded-full">
            {item.category}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="pt-20">
        <Section title="Our Work" bg="bg-slate-50">
          <p className="text-center text-lg text-gray-700 mb-12 max-w-3xl mx-auto">
            Browse through our completed projects and recent work. Click on any image to view full size.
          </p>

          {/* Featured Projects */}
          {hasProjects && (
            <div className="mb-16">
              {projects.map((project) => (
                <div key={project.id} className="mb-12">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-slate-800">{project.name}</h3>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {project.location && (
                        <span className="text-sm text-gray-500">{project.location}</span>
                      )}
                      {project.location && project.date && (
                        <span className="text-sm text-gray-300">|</span>
                      )}
                      {project.date && (
                        <span className="text-sm text-gray-500">{project.date}</span>
                      )}
                    </div>
                    {project.description && (
                      <p className="text-gray-600 mt-1 text-sm">{project.description}</p>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {project.images.map(renderImageCard)}
                  </div>
                </div>
              ))}

              {/* Divider between projects and remaining gallery */}
              {ungroupedImages.length > 0 && (
                <div className="border-t border-gray-200 pt-12 mt-4">
                  <h3 className="text-2xl font-bold text-slate-800 text-center mb-2">More Photos</h3>
                  <p className="text-center text-gray-500 mb-8">Additional work from around Highlands County</p>
                </div>
              )}
            </div>
          )}

          {/* Category filter tabs — for ungrouped images */}
          {hasCategories && ungroupedImages.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  activeCategory === 'all'
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                All ({ungroupedImages.length})
              </button>
              {categories.map((cat) => {
                const count = ungroupedImages.filter(item => item.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                      activeCategory === cat
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {cat} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Ungrouped images grid */}
          {filteredUngrouped.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredUngrouped.map(renderImageCard)}
            </div>
          )}

          {filteredUngrouped.length === 0 && ungroupedImages.length > 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No images in this category.</p>
              <button onClick={() => setActiveCategory('all')} className="text-green-600 font-semibold mt-2 hover:underline">
                Show all images
              </button>
            </div>
          )}

          {allImages.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No images found in the gallery.</p>
            </div>
          )}

          <div className="text-center mt-16">
            <Button onClick={() => { trackCtaClick('/contact', 'Get In Touch'); navigate('/contact'); }} variant="secondary">
              Get In Touch
            </Button>
          </div>
        </Section>
      </div>

      {selectedImage && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          imageUrl={selectedImage.full}
          altText={selectedImage.alt}
          caption={selectedImage.alt}
        />
      )}
    </>
  );
};

export default GalleryPage;
